import { createSelector } from "@reduxjs/toolkit";

const selectAppraisalData = (state) => state.appraisal.data;

const getUploadedImages = createSelector(
  selectAppraisalData,
  (data) => data.images
);

export const selectImageUploadData = (state) => state.imageUploads;

export const getLocalImages = createSelector(
  selectImageUploadData,
  (data) => data.images
);

export const getRequiredPhotoData = createSelector(
  selectImageUploadData,
  (data) => data.requiredPhotoData
);

const getFlattenedUploadedImageTags = createSelector(
  getUploadedImages,
  (uploadedImages) =>
    // flattens uploaded images down to an array like ["vehicle introduction", "front", "vehicle introduction", "side"]
    uploadedImages
      .reduce((acc, val) => acc.concat(val), [])
      .map((image) => image.imageTags)
      .reduce((acc, val) => acc.concat(val), [])
      .map((tags) => tags.tagDescription)
);

export const hasRequiredImagesForCategory = (category) =>
  createSelector(
    getLocalImages,
    getRequiredPhotoData,
    (localImages, imagesRequiredData) => {
      const flattendLocalTags = localImages
        .reduce((acc, val) => acc.concat(val), [])
        .map((image) => image.group);

      const uploadedImagesForGroup = (group) =>
        flattendLocalTags.filter((tag) => tag === group).length;

      return (
        uploadedImagesForGroup(category.id) >= category.imageQuantityRequired
      );
    }
  );

export const getNextPhotoGroup = createSelector(
  getLocalImages,
  getRequiredPhotoData,
  (localImages, imagesRequiredData) => {
    const flattendLocalTags = localImages
      .reduce((acc, val) => acc.concat(val), [])
      .map((image) => image.group);

    const uploadedImagesForGroup = (group) =>
      flattendLocalTags.filter((tag) => tag === group).length;
    return imagesRequiredData.find(
      (group) => uploadedImagesForGroup(group.id) < group.imageQuantityRequired
    );
  }
);

export const getHasAllRequiredPhotosUploaded = createSelector(
  getFlattenedUploadedImageTags,
  getRequiredPhotoData,
  (uploadedImageTags, imagesRequiredData) => {
    const uploadedImagesForGroup = (group) =>
      uploadedImageTags.filter((tag) => tag === group).length;
    return imagesRequiredData.every(
      (group) => uploadedImagesForGroup(group.id) >= group.imageQuantityRequired
    );
  }
);

export const getImageGroupCount = (group) =>
  createSelector(getFlattenedUploadedImageTags, (uploadedImageTags) => {
    return uploadedImageTags.filter((tag) => tag === group.id).length;
  });

export const getHasImagesFlaggedForDeletion = createSelector(
  getLocalImages,
  (images) => images.some((image) => image.flaggedForDeletion === true)
);

export const getFrontUserUpload = createSelector(
  getLocalImages,
  (images) =>
    images?.find((image) => image?.group === "front")?.imageSrc || null
);

export const getImageFlaggedForDeletion = (image) =>
  createSelector(getLocalImages, (images) => {
    const foundImage = images.find(
      (imageInArray) => imageInArray.imageIdentifier === image.imageIdentifier
    );
    return foundImage.flaggedForDeletion;
  });

export const getHasExtraImagesForGroup = (image) =>
  createSelector(getImageGroupCount(image.group), (imagesUploaded) => {
    return imagesUploaded > image.qtyRequired;
  });

export const getImagesGroupArray = createSelector(
  selectImageUploadData,
  (data) => data.images.map((image) => image.group)
);

export const getRequiredPhotosForGroup = (group) =>
  createSelector(
    getLocalImages,
    getRequiredPhotoData,
    (localImages, imagesRequiredData) => {
      const flattendLocalTags = localImages
        .reduce((acc, val) => acc.concat(val), [])
        .map((image) => image.group);

      const uploadedImagesForGroup = (group) =>
        flattendLocalTags.filter((tag) => tag === group).length;

      return group.imageQuantityRequired - uploadedImagesForGroup(group.id);
    }
  );
